import React, { useEffect, useState, useRef } from 'react';
import { Save, Eye, Send, Download, RotateCcw, Mail, Code as CodeIcon } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const TYPES = [
  {
    key: 'receipt',
    label: 'Bağış Makbuzu',
    description: 'Bağış başarılı olduğunda bağışçıya gönderilen teşekkür/makbuz e-postası.',
    variables: [
      { k: 'name', d: 'Bağışçının adı' },
      { k: 'donor_name', d: 'Bağışçının adı (alternatif)' },
      { k: 'amount', d: 'Bağış tutarı (ondalıklı, ör: 500.00)' },
      { k: 'amount_int', d: 'Tam sayı tutar (ör: 500)' },
      { k: 'payment_id', d: 'İşlem numarası' },
      { k: 'occasion_label', d: 'Özel gün etiketi (varsa)' },
    ],
  },
  {
    key: 'special_card',
    label: 'Özel Gün Kartı',
    description: 'Özel gün bağışlarında alıcıya gönderilen kart e-postası.',
    variables: [
      { k: 'donor_name', d: 'Bağışı yapanın adı' },
      { k: 'to_name', d: 'Kartın gideceği kişinin adı' },
      { k: 'note', d: 'Bağışçının yazdığı kişisel mesaj' },
      { k: 'occasion_label', d: 'Özel gün adı (Doğum Günü, Yıl Dönümü vb.)' },
      { k: 'amount', d: 'Bağış tutarı (ondalıklı)' },
      { k: 'amount_int', d: 'Tam sayı tutar' },
      { k: 'design', d: 'Kart deseni (pastel, gold, navy, cream)' },
    ],
  },
];

export default function AdminEmailTemplates() {
  const [activeType, setActiveType] = useState('receipt');
  const [subjectTpl, setSubjectTpl] = useState('');
  const [htmlTpl, setHtmlTpl] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [savedSubject, setSavedSubject] = useState('');
  const [savedHtml, setSavedHtml] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  const cur = TYPES.find(t => t.key === activeType);

  // Load saved template for type
  const loadSaved = async () => {
    try {
      const list = await api.adminListSettings();
      const m = {};
      list.forEach(s => { m[s.key] = s.value; });
      const sub = m[`email_tpl_${activeType}_subject`] || '';
      const html = m[`email_tpl_${activeType}_html`] || '';
      setSavedSubject(sub);
      setSavedHtml(html);
      setSubjectTpl(sub);
      setHtmlTpl(html);
    } catch {
      toast.error('Şablon yüklenemedi');
    }
  };

  useEffect(() => { loadSaved(); /* eslint-disable-next-line */ }, [activeType]);

  // Debounced live preview
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.adminEmailPreview({
          type: activeType,
          template_subject: subjectTpl || null,
          template_html: htmlTpl || null,
        });
        setPreviewSubject(r.subject);
        setPreviewHtml(r.html);
      } catch (e) {
        // ignore preview errors silently
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [activeType, subjectTpl, htmlTpl]);

  const save = async () => {
    setBusy(true);
    try {
      await Promise.all([
        api.adminUpsertSetting(`email_tpl_${activeType}_subject`, subjectTpl),
        api.adminUpsertSetting(`email_tpl_${activeType}_html`, htmlTpl),
      ]);
      setSavedSubject(subjectTpl);
      setSavedHtml(htmlTpl);
      toast.success(`${cur.label} şablonu kaydedildi`);
    } catch {
      toast.error('Kaydedilemedi');
    } finally { setBusy(false); }
  };

  const loadDefault = async () => {
    if (!window.confirm('Varsayılan şablon mevcut düzenlemenin üzerine yazılacak. Emin misiniz?')) return;
    try {
      const r = await api.adminEmailDefaults(activeType);
      setSubjectTpl(r.subject);
      setHtmlTpl(r.html);
      toast.success('Varsayılan şablon editöre yüklendi (henüz kaydedilmedi)');
    } catch {
      toast.error('Varsayılan şablon alınamadı');
    }
  };

  const resetSaved = () => {
    setSubjectTpl(savedSubject);
    setHtmlTpl(savedHtml);
    toast.info('Düzenlemeler iptal edildi');
  };

  const sendTest = async () => {
    if (busy) return;
    if (!testEmail || !testEmail.includes('@')) { toast.error('Geçerli bir e-posta girin'); return; }
    setBusy(true);
    try {
      // Save first so the test mail uses the latest draft
      await Promise.all([
        api.adminUpsertSetting(`email_tpl_${activeType}_subject`, subjectTpl),
        api.adminUpsertSetting(`email_tpl_${activeType}_html`, htmlTpl),
      ]);
      const r = await api.adminEmailTest({ to: testEmail, type: activeType });
      if (r.sent) toast.success(`Test maili ${testEmail} adresine gönderildi`);
      else toast.warning(`Mail gönderilemedi: RESEND_API_KEY yapılandırılmamış olabilir.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test maili gönderilemedi');
    } finally { setBusy(false); }
  };

  const insertVariable = (k) => {
    const text = `{{${k}}}`;
    setHtmlTpl(s => s + text);
  };

  const dirty = subjectTpl !== savedSubject || htmlTpl !== savedHtml;

  return (
    <div data-testid="admin-email-templates">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">İletişim</div>
          <h1 className="font-display text-4xl mt-2 leading-none">E-posta Şablonları</h1>
          <p className="mt-2 text-ink-400 text-sm max-w-3xl">
            Otomatik e-postaların konu ve içeriğini buradan düzenleyin. <code className="text-xs bg-cream-200 px-1.5 py-0.5 rounded">{'{{name}}'}</code> gibi değişkenleri istediğiniz yere yerleştirebilirsiniz.
          </p>
        </div>
      </div>

      {/* Type tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {TYPES.map(t => (
          <button key={t.key} onClick={() => setActiveType(t.key)} data-testid={`tpl-${t.key}`}
            className={`px-5 h-11 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${activeType === t.key ? 'bg-ink-600 text-cream' : 'bg-white border border-ink-100 text-ink-500 hover:border-ink-300'}`}>
            <Mail className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <p className="mb-5 text-sm text-ink-400">{cur.description}</p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="bg-white rounded-2xl border border-ink-100 p-6 self-start">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold flex items-center gap-2"><CodeIcon className="w-3.5 h-3.5" /> HTML Şablonu</div>
            <div className="flex items-center gap-1">
              <button onClick={loadDefault} className="text-xs text-ink-500 hover:text-ink-700 px-2 h-8 rounded-lg hover:bg-cream-200" title="Varsayılan şablonu yükle">
                <Download className="w-3.5 h-3.5 inline mr-1" /> Varsayılan
              </button>
              {dirty && (
                <button onClick={resetSaved} className="text-xs text-ink-500 hover:text-coral-300 px-2 h-8 rounded-lg hover:bg-cream-200">
                  <RotateCcw className="w-3.5 h-3.5 inline mr-1" /> İptal
                </button>
              )}
            </div>
          </div>

          <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold">Konu (Subject)</label>
          <input
            value={subjectTpl}
            onChange={e => setSubjectTpl(e.target.value)}
            placeholder={`Örn: YEKÜV Bağış Makbuzu — {{occasion_label}}`}
            className="mt-2 w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-11 text-sm outline-none font-mono"
            data-testid="tpl-subject"
          />

          <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold mt-5 block">HTML İçerik</label>
          <textarea
            value={htmlTpl}
            onChange={e => setHtmlTpl(e.target.value)}
            placeholder="Boş bırakılırsa kodda yazılı varsayılan kullanılır. 'Varsayılan' butonuyla başlangıç şablonu yükleyebilirsiniz."
            rows={18}
            className="mt-2 w-full bg-ink-900 text-cream rounded-xl px-3 py-3 text-xs outline-none resize-y font-mono leading-relaxed"
            data-testid="tpl-html"
            style={{ minHeight: 380 }}
          />

          <div className="mt-4 p-4 rounded-xl bg-cream-200 border border-ink-100">
            <div className="text-[10px] tracking-[0.25em] uppercase text-ink-500 font-bold mb-2">Kullanılabilir Değişkenler (tıklayarak ekle)</div>
            <div className="flex flex-wrap gap-1.5">
              {cur.variables.map(v => (
                <button key={v.k} onClick={() => insertVariable(v.k)} className="px-2.5 py-1 rounded-md bg-white border border-ink-100 text-xs font-mono text-ink-600 hover:bg-ink-600 hover:text-cream hover:border-ink-600 transition-colors" title={v.d}>
                  {`{{${v.k}}}`}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={save} disabled={busy} data-testid="save-tpl" className="bg-ink-600 text-cream rounded-full px-6 h-11 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-2 disabled:opacity-60">
              <Save className="w-4 h-4" /> {busy ? 'Kaydediliyor...' : 'Şablonu Kaydet'}
            </button>
            <div className="flex-1" />
            <input
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="Test maili için e-posta..."
              className="flex-1 min-w-[200px] bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-full px-4 h-11 text-sm outline-none"
              type="email"
            />
            <button onClick={sendTest} disabled={busy || !testEmail} data-testid="send-test" className="bg-gold text-ink-700 rounded-full px-5 h-11 text-sm font-semibold hover:bg-gold-400 inline-flex items-center gap-2 disabled:opacity-60">
              <Send className="w-4 h-4" /> Test Gönder
            </button>
          </div>
        </div>

        {/* Live preview */}
        <div className="bg-white rounded-2xl border border-ink-100 overflow-hidden self-start lg:sticky lg:top-20">
          <div className="px-5 py-4 border-b border-ink-100 flex items-center gap-2">
            <Eye className="w-4 h-4 text-coral-300" />
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Canlı Önizleme</div>
              <div className="text-xs text-ink-400 mt-0.5">Örnek değerlerle render edilmiş hali</div>
            </div>
          </div>
          <div className="px-5 py-3 border-b border-ink-100 bg-cream-100">
            <div className="text-[10px] tracking-widest uppercase text-ink-400">Konu</div>
            <div className="text-sm font-semibold text-ink-700 mt-1 truncate" title={previewSubject}>{previewSubject || '—'}</div>
          </div>
          <div className="bg-cream-200 overflow-auto" style={{ maxHeight: 700 }}>
            <iframe
              title="email-preview"
              srcDoc={previewHtml || '<div style="padding:40px;font-family:sans-serif;color:#999;text-align:center">Önizleme yükleniyor...</div>'}
              className="w-full"
              style={{ minHeight: 600, border: 'none', background: '#F4EEE2' }}
              sandbox=""
            />
          </div>
        </div>
      </div>
    </div>
  );
}
